import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import { mdiPlus } from '@mdi/js';
import { useApolloClient, useMutation } from '@apollo/client/react';

import {
    AddUsersToTrainingDocument,
    ITrainingFragment,
    ITrainingRole,
    IUserListItemFragment,
} from 'graphql/types';
import { ActionButtonDropdown } from 'common/components/ActionButtonDropdown';
import { FilePicker } from 'common/components/FilePicker';
import { Icon } from 'common/components/Icon';
import { InlineStickyWrapper } from 'common/components/InlineStickyWrapper';
import { Switch } from 'common/components/Switch';
import { useApolloError } from 'common/hooks/useApolloError';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { IGlobalDrawerType } from 'common/types';
import { UserImportBlockedDialog } from 'user/components/UserImportBlockedDialog';
import { UserImportModal } from 'user/components/UserImportModal';
import { useFrontendPermissions } from 'user/hooks';
import { useUserImportGuard } from 'user/hooks/useUserImportGuard';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { useUserImport } from 'user/hooks/useUserImport';
import { parseJSON } from 'common/utils/json';
import { EUsersImportType, ICreateUserFormValues } from 'user/types';

interface IAddUsersHandlers {
    onAddUsers: (
        selectedUsers: string[],
        selectableUsers?: IUserListItemFragment[]
    ) => Promise<void>;
    loadingAddUsers: boolean;
    isSelectorOpen: boolean;
    onSelectorOpenChange: (open: boolean) => void;
    selectDrawerFooterAppend: ReactNode;
}

interface IProps {
    training: ITrainingFragment;
    onParticipantsAdded: () => void;
    children: (handlers: IAddUsersHandlers) => ReactNode;
}

export const AddUsersButton = ({
    training,
    onParticipantsAdded,
    children,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const { openGlobalDrawer } = useGlobalDrawer();
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [sendInviteMail, setSendInviteMail] = useState(true);

    const {
        completeImportData,
        importModalOpen,
        handleFileUpload,
        handleImportModalClose,
    } = useUserImport({
        onUploadError: (_fileName, message) => {
            displaySnackbar(message, { variant: 'error' });
        },
    });

    const { canCreate: canCreateParticipants } = useFrontendPermissions(
        'trainingParticipants'
    );
    const { canCreateUser = false } = useAppPermissions(
        'general',
        'management'
    );

    const { id: trainingId, permissions } = training;

    const permissionsObj = parseJSON(permissions || '');
    const { canImportUsers = false } = permissionsObj || {};

    const {
        guardedImportClick,
        checkingImports,
        importBlockedDialogOpen,
        handleImportBlockedClose,
    } = useUserImportGuard({ canImport: canImportUsers });

    const [addUsersToTraining, { loading: loadingAddUsers }] = useMutation(
        AddUsersToTrainingDocument
    );

    const showCreateUser = canCreateParticipants && canCreateUser;

    const handleAddParticipants = useCallback(
        async (
            selectedParticipantIds: string[],
            selectableParticipants?: IUserListItemFragment[],
            sendInvites?: boolean
        ) => {
            try {
                const response = await addUsersToTraining({
                    variables: {
                        trainingId,
                        userIds: selectedParticipantIds,
                        role: ITrainingRole.Participant,
                        sendInvites,
                    },
                    update(cache) {
                        cache.modify({
                            id: cache.identify(training),
                            fields: {
                                participants(
                                    _existingParticipants,
                                    { DELETE }
                                ) {
                                    return DELETE;
                                },
                            },
                        });
                    },
                });

                const { data } = response;
                const { addedIds, alreadyAddedIds, failedIds } =
                    data?.addUsersToTraining || {};

                if (!!addedIds?.length) {
                    displaySnackbar(
                        translate(
                            'trainingParticipants.participantsAddedSuccess',
                            { count: addedIds.length }
                        ),
                        { variant: 'success' }
                    );

                    // Evict cached user lists so the user selector reflects the updated participation status
                    client.cache.evict({ fieldName: 'paginatedUsers' });
                    client.cache.gc();
                }

                if (
                    !!alreadyAddedIds?.length &&
                    !!selectableParticipants?.length
                ) {
                    const alreadyAddedParticipants =
                        selectableParticipants.filter((participant) =>
                            alreadyAddedIds.includes(participant.id)
                        );

                    const alreadyAddedParticipantNames =
                        alreadyAddedParticipants
                            .map((participant) => participant.name)
                            .join(', ');

                    displaySnackbar(
                        translate(
                            'trainingParticipants.participantsAlreadyAdded',
                            {
                                participants: alreadyAddedParticipantNames,
                                count: alreadyAddedParticipants.length,
                                interpolation: { escapeValue: false },
                            }
                        ),
                        { variant: 'error' }
                    );
                }

                if (failedIds?.length && !!selectableParticipants?.length) {
                    const failedParticipants = selectableParticipants.filter(
                        (participant) => failedIds.includes(participant.id)
                    );

                    const failedParticipantNames = failedParticipants
                        .map((participant) => participant.name)
                        .join(', ');

                    displaySnackbar(
                        translate(
                            'trainingParticipants.participantsAddedFailed',
                            {
                                participants: failedParticipantNames,
                                count: failedParticipants.length,
                                interpolation: { escapeValue: false },
                            }
                        ),
                        { variant: 'error' }
                    );
                }
                if (!sendInviteMail) setSendInviteMail(true);

                onParticipantsAdded();
                setIsSelectorOpen(false);
            } catch (error) {
                showApolloError(error);
            }
        },
        [
            addUsersToTraining,
            trainingId,
            training,
            displaySnackbar,
            translate,
            client,
            showApolloError,
            sendInviteMail,
            onParticipantsAdded,
        ]
    );

    const handleOpenCreateDrawer = useCallback(() => {
        openGlobalDrawer({
            type: IGlobalDrawerType.UserCreate,
            props: {
                forTraining: true,
                onCreate: (
                    userId: string,
                    formValues: ICreateUserFormValues
                ) => {
                    const sendTrainingInvite = formValues.sendTrainingInvite;

                    handleAddParticipants(
                        [userId],
                        undefined,
                        sendTrainingInvite
                    );
                },
            },
        });
    }, [openGlobalDrawer, handleAddParticipants]);

    const handleImportFile = useCallback(() => {
        guardedImportClick('training-users-import-csv');
    }, [guardedImportClick]);

    const handleImportSuccess = useCallback(
        (_succeededCount: number) => {
            // Evict cached user lists since the import may have created new users,
            // ensuring the user selector is also refreshed
            client.cache.evict({ fieldName: 'paginatedUsers' });
            client.cache.gc();
            client.refetchQueries({ include: ['TrainingParticipants'] });

            onParticipantsAdded();
        },
        [client, onParticipantsAdded]
    );

    const onAddUsers = useCallback(
        (selectedUsers: string[], selectableUsers?: IUserListItemFragment[]) =>
            handleAddParticipants(
                selectedUsers,
                selectableUsers,
                sendInviteMail
            ),
        [handleAddParticipants, sendInviteMail]
    );

    const options = [
        ...(canCreateParticipants
            ? [
                  {
                      key: 'add-existing',
                      label: translate(
                          'trainingParticipants.addExistingParticipants'
                      ),
                      onClick: () => setIsSelectorOpen(true),
                  },
              ]
            : []),
        ...(showCreateUser
            ? [
                  {
                      key: 'create-new',
                      label: translate(
                          'trainingParticipants.addNewParticipant'
                      ),
                      onClick: handleOpenCreateDrawer,
                  },
              ]
            : []),
        ...(canImportUsers
            ? [
                  {
                      key: 'import-file',
                      label: translate(
                          'trainingParticipants.importParticipants'
                      ),
                      onClick: handleImportFile,
                  },
              ]
            : []),
    ];

    const [primaryOption] = options;

    const menuItems = options.map((option) => (
        <MenuItem key={option.key} onClick={option.onClick}>
            {option.label}
        </MenuItem>
    ));

    const selectDrawerFooterAppend = (
        <Box sx={{ mb: 2, textAlign: 'left' }}>
            <Switch
                checked={sendInviteMail}
                label={translate('sendUserTrainingInvite')}
                onChange={() => setSendInviteMail(!sendInviteMail)}
            />
        </Box>
    );

    return (
        <>
            {!!options.length && (
                <>
                    <InlineStickyWrapper fullWidth leftOffset={3}>
                        <ActionButtonDropdown
                            enablePortal
                            disabled={checkingImports}
                            menuItems={
                                options.length > 1 ? menuItems : undefined
                            }
                            size="medium"
                            onClick={primaryOption.onClick}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {checkingImports ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : (
                                    <Icon path={mdiPlus} />
                                )}
                            </Box>

                            {primaryOption.label}
                        </ActionButtonDropdown>
                    </InlineStickyWrapper>

                    <UserImportBlockedDialog
                        open={importBlockedDialogOpen}
                        onClose={handleImportBlockedClose}
                    />

                    {canImportUsers && (
                        <>
                            <FilePicker
                                id="training-users-import-csv"
                                inputProps={{ accept: '.csv' }}
                                sx={{
                                    position: 'absolute',
                                    overflow: 'hidden',
                                    width: 0,
                                    height: 0,
                                }}
                                onUpload={handleFileUpload}
                            />

                            <UserImportModal
                                forRelatedIds={[trainingId]}
                                importData={completeImportData}
                                importType={EUsersImportType.Training}
                                open={importModalOpen}
                                onClose={handleImportModalClose}
                                onSuccess={handleImportSuccess}
                            />
                        </>
                    )}
                </>
            )}

            {children({
                onAddUsers,
                loadingAddUsers,
                isSelectorOpen,
                onSelectorOpenChange: setIsSelectorOpen,
                selectDrawerFooterAppend,
            })}
        </>
    );
};
